import type { GatewayCredential, GatewayEndpoint, TranscriptMessage } from "@timibot/core";
import { keyAccount } from "@timibot/core";

/** 已保存的连接（非敏感部分）。 */
export interface ServerProfile {
  endpoint: GatewayEndpoint;
  /** 登录时使用的用户名，方便重新登录时预填 */
  username?: string;
  authProvider?: string;
  lastKnownVersion?: string;
  /** 在主机上的身份："owner"（主人）/ "guest"（访客，只能用分享给它的 Bot）；没有 = 主人 */
  role?: string;
  /** 访客可用的 Bot（profile 名） */
  bots?: string[];
}

export function isGuestProfile(p: ServerProfile | undefined): boolean {
  return p?.role === "guest";
}

/** 同一台主机上的不同身份算不同的连接 */
export function connectionID(p: ServerProfile): string {
  return p.endpoint.kind === "relay" ? keyAccount(p.endpoint.relay) : "demo";
}

const PREFIX = "timibot.";

function read<T>(key: string): T | undefined {
  try {
    const raw = localStorage.getItem(PREFIX + key);
    return raw === null ? undefined : (JSON.parse(raw) as T);
  } catch {
    return undefined;
  }
}

function write(key: string, value: unknown): void {
  try {
    if (value === undefined || value === null) localStorage.removeItem(PREFIX + key);
    else localStorage.setItem(PREFIX + key, JSON.stringify(value));
  } catch {
    /* 私密模式或存满了：只在这次打开期间有效 */
  }
}

/** 轻量的本地偏好（对应 iOS 的 Preferences）。 */
export const Preferences = {
  get server(): ServerProfile | undefined { return read("server"); },
  set server(v: ServerProfile | undefined) { write("server", v); },

  get savedServers(): ServerProfile[] { return read("savedServers") ?? []; },
  set savedServers(v: ServerProfile[]) { write("savedServers", v); },

  /** 当前选中的 Bot（profile 名；没有 = 默认） */
  get selectedProfile(): string | undefined { return read("selectedProfile"); },
  set selectedProfile(v: string | undefined) { write("selectedProfile", v); },

  /** 上次打开的会话（存储 id），启动时恢复 */
  get lastSessionID(): string | undefined { return read("lastSessionID"); },
  set lastSessionID(v: string | undefined) { write("lastSessionID", v); },

  get notifyOnCompletion(): boolean { return read<boolean>("notifyOnCompletion") ?? true; },
  set notifyOnCompletion(v: boolean) { write("notifyOnCompletion", v); },

  /** 语音输入（浏览器自带识别）：默认开 */
  get voiceEnabled(): boolean { return read<boolean>("voiceEnabled") ?? true; },
  set voiceEnabled(v: boolean) { write("voiceEnabled", v); },

  /** 主题：system / light / dark */
  get theme(): "system" | "light" | "dark" { return read("theme") ?? "system"; },
  set theme(v: "system" | "light" | "dark") { write("theme", v); },
};

/**
 * 凭据（登录门禁时的 token）。网页没有钥匙串，只能存在浏览器本地：只有这个网站能读，不会发给任何服务器。
 * `connection` 为空时是当前连接的凭据；其它已保存连接的凭据按连接 id 分开存。
 */
export const CredentialVault = {
  load(connection?: string): GatewayCredential | undefined {
    return read(connection ? `credential.saved.${connection}` : "credential");
  },
  save(credential: GatewayCredential | undefined, connection?: string): void {
    write(connection ? `credential.saved.${connection}` : "credential", credential);
  },
};

/**
 * 最近打开过的对话在本机的副本：冷启动时先把上次的对话画出来，连上后再用服务器的版本替换。
 * 只是显示用，服务器永远是准的。
 */
export const TranscriptCache = {
  messageLimit: 150,
  sessionLimit: 30,

  load(storedID: string): { title?: string; messages: TranscriptMessage[] } | undefined {
    const entry = read<{ title?: string; messages: TranscriptMessage[] }>(`transcript.${storedID}`);
    return entry && entry.messages?.length ? entry : undefined;
  },

  save(storedID: string, title: string | undefined, messages: TranscriptMessage[]): void {
    if (!messages.length) return;
    write(`transcript.${storedID}`, { title, messages: messages.slice(-this.messageLimit) });
    const order = (read<string[]>("transcriptOrder") ?? []).filter((id) => id !== storedID);
    order.unshift(storedID);
    for (const old of order.slice(this.sessionLimit)) write(`transcript.${old}`, undefined);
    write("transcriptOrder", order.slice(0, this.sessionLimit));
  },

  remove(storedID: string): void {
    write(`transcript.${storedID}`, undefined);
  },
};
